// App entry
import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AppLayout } from "@/components/AppLayout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PublicLayout } from "@/components/PublicLayout";
import PublicHomePage from "@/pages/PublicHomePage";
import AdminProjectDetailPage from "@/pages/admin/AdminProjectDetailPage";
import AuthPage from "@/pages/AuthPage";
import OAuthConsentPage from "@/pages/OAuthConsentPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import UnsubscribePage from "@/pages/UnsubscribePage";
import WebinarRedirectPage from "@/pages/public/WebinarRedirectPage";
import ForcePasswordResetPage from "@/pages/ForcePasswordResetPage";
import DashboardRouter from "@/components/DashboardRouter";
import DiagnosticPage from "@/pages/participant/DiagnosticPage";

import ContentPage from "@/pages/participant/ContentPage";
import { contentHref } from "@/lib/liaContentLinks";
import { ChallengePage } from "@/pages/participant/ChallengePage";
import ChallengeLabPage from "@/pages/participant/ChallengeLabPage";
import { ProjectDetailPage } from "@/pages/participant/ProjectDetailPage";
import ShowcasePage from "@/pages/participant/ShowcasePage";

import MetricsPage from "@/pages/participant/MetricsPage";
import NewsPage from "@/pages/participant/NewsPage";
import CalendarPage from "@/pages/participant/CalendarPage";
import ChangeManagementPage from "@/pages/participant/ChangeManagementPage";
import AIToolsPage from "@/pages/participant/AIToolsPage";
import ProfilePage from "@/pages/participant/ProfilePage";
import SupportPage from "@/pages/participant/SupportPage";

import { MentorDashboard } from "@/pages/mentor/MentorDashboard";
import { MentorParticipantsPage } from "@/pages/mentor/ParticipantsPage";
import MentorCalendarPage from "@/pages/mentor/MentorCalendarPage";
import StudentsPage from "@/pages/mentor/StudentsPage";
import { AdminDashboard } from "@/pages/admin/AdminDashboard";
import ExecutiveDashboardPage from "@/pages/admin/executive/ExecutiveDashboardPage";
import { DiagnosticMetricsPage } from "@/pages/admin/metrics/DiagnosticMetricsPage";
import { ProjectMetricsPage } from "@/pages/admin/metrics/ProjectMetricsPage";
import { DeliverablesMetricsPage } from "@/pages/admin/metrics/DeliverablesMetricsPage";

import TicketManagementPage from "@/pages/admin/TicketManagementPage";
import AIPortfolioPage from "@/pages/admin/AIPortfolioPage";
import AIConversationsAuditPage from "@/pages/admin/AIConversationsAuditPage";
import EnterpriseDashboardPage from "@/pages/admin/EnterpriseDashboardPage";
import EnterpriseDiagnosticPage from "@/pages/admin/EnterpriseDiagnosticPage";
import AIInventoryPage from "@/pages/admin/AIInventoryPage";
import CohortManagementPage from "@/pages/admin/CohortManagementPage";
import UserManagementPage from "@/pages/admin/UserManagementPage";
import AcquisitionDashboardPage from "@/pages/admin/AcquisitionDashboardPage";
import UserTrackingPage from "@/pages/admin/UserTrackingPage";
import RiskManagementPage from "@/pages/admin/RiskManagementPage";
import CoEManagementPage from "@/pages/admin/CoEManagementPage";
import ContentManagementPage from "@/pages/admin/ContentManagementPage";
import BulkCompletionPage from "@/pages/admin/BulkCompletionPage";
import TrendsManagementPage from "@/pages/admin/TrendsManagementPage";
import WebTrendsPage from "@/pages/admin/WebTrendsPage";

import AIEvaluationsPage from "@/pages/admin/AIEvaluationsPage";
import ResourceManagementPage from "@/pages/admin/ResourceManagementPage";

import ProgramManagementPage from "@/pages/admin/ProgramManagementPage";
import RouteInterestsPage from "@/pages/admin/RouteInterestsPage";

import NPSDashboardPage from "@/pages/admin/NPSDashboardPage";
import PlatformConfigPage from "@/pages/admin/PlatformConfigPage";
import WhatsAppTestPage from "@/pages/admin/WhatsAppTestPage";
import WhatsAppLogsPage from "@/pages/admin/WhatsAppLogsPage";
import DiscountCodesPage from "@/pages/admin/DiscountCodesPage";
import PaymentsDashboardPage from "@/pages/admin/PaymentsDashboardPage";
import OrganizationsPage from "@/pages/admin/OrganizationsPage";
import { EnterpriseSettingsPage } from "@/pages/admin/EnterpriseSettingsPage";
import ForumPage from "@/pages/forum/ForumPage";
import ThreadDetailPage from "@/pages/forum/ThreadDetailPage";
import PlansPage from "@/pages/landing/PlansPage";


import EnterprisePage from "@/pages/landing/EnterprisePage";
import EnterpriseDiagnosticLandingPage from "@/pages/public/EnterpriseDiagnosticLandingPage";
import EnterpriseSignupPage from "@/pages/public/EnterpriseSignupPage";
import { UpgradePlanScreen } from "@/components/admin/UpgradePlanScreen";
import { useOrgPlan } from "@/hooks/useOrgPlan";
import PublicContentPage from "@/pages/public/PublicContentPage";
import PublicDiagnosticPage from "@/pages/public/PublicDiagnosticPage";
import PublicProjectsPage from "@/pages/public/PublicProjectsPage";
import PublicCertificationsPage from "@/pages/public/PublicCertificationsPage";
import CertificationDetailPage from "@/pages/public/CertificationDetailPage";
import PublicForumPage from "@/pages/public/PublicForumPage";
import EnrollmentPage from "@/pages/public/EnrollmentPage";
import EnrollmentResultPage from "@/pages/public/EnrollmentResultPage";
import PrivacyPolicyPage from "@/pages/public/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/public/TermsOfServicePage";
import CertificateVerifyPage from "@/pages/public/CertificateVerifyPage";
import ImpactPage from "@/pages/public/ImpactPage";
import AboutPage from "@/pages/public/AboutPage";
import ImpactManagementPage from "@/pages/admin/ImpactManagementPage";
import LiveSessionsAuditPage from "@/pages/admin/LiveSessionsAuditPage";
import BulkEmailPage from "@/pages/admin/BulkEmailPage";
import BulkUnsubscribePage from "@/pages/BulkUnsubscribePage";
import PayoutsPage from "@/pages/admin/PayoutsPage";
import PayoutDetailPage from "@/pages/admin/PayoutDetailPage";
import ResourceWorkshopsListPage from "@/pages/admin/ResourceWorkshopsListPage";
import ResourceWorkshopEditPage from "@/pages/admin/ResourceWorkshopEditPage";
import CustomMentorshipsPage from "@/pages/mentorships/CustomMentorshipsPage";
import UnifiedCalendarPage from "@/pages/calendar/UnifiedCalendarPage";
import MentorshipProjectDetailPage from "@/pages/mentorships/MentorshipProjectDetailPage";
import MentorshipGroupDetailPage from "@/pages/mentorships/MentorshipGroupDetailPage";
import ResourceWorkshopPage from "@/pages/public/ResourceWorkshopPage";
import NotFound from "./pages/NotFound";
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics";
import { useReferralAttribution } from "@/hooks/useReferralAttribution";
import { useUtmAttribution } from "@/hooks/useUtmAttribution";


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,   // datos frescos por 5 min — evita re-fetches innecesarios
      refetchOnWindowFocus: false, // no recargar al volver a la pestaña
      retry: 1,
    },
  },
});

// El rol Auditor (solo lectura) solo puede acceder a sus 4 módulos + su perfil.
// Si intenta abrir cualquier otra ruta admin por URL, se lo redirige.
const AUDITOR_ALLOWED_PATHS = [
  '/user-management',
  '/cohort-management',
  '/ai-portfolio',
  '/enterprise-dashboard',
  '/profile',
];

function ProtectedAppRoute({ children }: { children: React.ReactNode }) {
  const { user, roles, currentRole, isLoading, rolesResolved } = useAuth();
  const location = useLocation();
  // Mismo patrón que SuperAdminRoute (abajo): isLoading se libera antes de
  // que lleguen los roles, por lo que sin esta guarda las páginas hijas
  // montaban con currentRole aún en null. Además de la redirección de
  // auditor (que ya evaluaba mal con roles=[]), varios hooks de datos de
  // estas páginas están gateados solo por `user`, no por `currentRole` — y
  // las policies RLS de auditor ahora exigen el header x-active-role que se
  // fija recién cuando currentRole resuelve. Sin esta guarda, un auditor
  // podía disparar esas queries antes de tiempo y quedarse con 0 filas
  // cacheadas hasta un refresh manual.
  // Solo esperamos mientras la consulta de roles no haya resuelto: si el
  // usuario no tiene rol asignado (o la consulta falló) igual renderizamos.
  if (isLoading || (user && roles.length === 0 && !rolesResolved)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  if (currentRole === 'auditor') {
    const path = location.pathname;
    const allowed = AUDITOR_ALLOWED_PATHS.some((p) => path === p || path.startsWith(p + '/'));
    if (!allowed) return <Navigate to="/user-management" replace />;
  }
  return (
    <ProtectedRoute>
      <AppLayout>{children}</AppLayout>
    </ProtectedRoute>
  );
}

function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { user, roles, isLoading } = useAuth();
  // Esperar a que los roles realmente terminen de cargar antes de evaluar el gate.
  // isLoading se libera antes de que llegue user_roles, por lo que sin esta guarda
  // hay una ventana en la que roles=[] y redirige a /dashboard por error.
  if (isLoading || (user && roles.length === 0)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  if (!roles.includes('super_admin')) {
    return <Navigate to="/dashboard" replace />;
  }
  return <ProtectedAppRoute>{children}</ProtectedAppRoute>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, roles, isLoading } = useAuth();
  if (isLoading || (user && roles.length === 0)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  if (!roles.includes('admin') && !roles.includes('super_admin')) {
    return <Navigate to="/dashboard" replace />;
  }
  return (
    <ProtectedAppRoute>
      <PlanGate>{children}</PlanGate>
    </ProtectedAppRoute>
  );
}

// Plan gratuito empresarial: los módulos de gestión y gobierno de IA se ven en
// el menú pero no se pueden abrir; si se entra por URL mostramos la invitación
// a mejorar de plan en lugar del módulo.
function PlanGate({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { canAccess, isLoading } = useOrgPlan();
  if (isLoading) return <>{children}</>;
  if (canAccess(location.pathname)) return <>{children}</>;
  return <UpgradePlanScreen />;
}

// Mentorías Personalizadas: visible para mentores y super admin.
function MentorshipRoute({ children, allowOrgAdmin = false }: { children: React.ReactNode; allowOrgAdmin?: boolean }) {
  const { user, roles, isLoading } = useAuth();
  if (isLoading || (user && roles.length === 0)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  if (!roles.includes('mentor') && !roles.includes('super_admin') && !(allowOrgAdmin && roles.includes('admin'))) {
    return <Navigate to="/dashboard" replace />;
  }
  return <ProtectedAppRoute>{children}</ProtectedAppRoute>;
}


// Redirección legacy: /certificaciones/:slug → /rutas/:slug
function LegacyCertificationRedirect() {
  const { certificationId } = useParams();
  return <Navigate to={`/rutas/${certificationId}`} replace />;
}

function LegacyContentRedirect() {
  const { contentId } = useParams();
  const { search } = useLocation();
  if (!contentId) return <Navigate to="/content" replace />;
  const params = new URLSearchParams(search);
  params.set('open', contentId);
  return <Navigate to={`${contentHref(contentId).split('?')[0]}?${params}`} replace />;
}

function AppRoutes() {
  const { user, isLoading } = useAuth();
  useGoogleAnalytics();
  useReferralAttribution();
  useUtmAttribution();


  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Root */}
      <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <PublicLayout><PublicHomePage /></PublicLayout>} />

      {/* Auth */}
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/.lovable/oauth/consent" element={<OAuthConsentPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route path="/comunicados/baja" element={<BulkUnsubscribePage />} />
      <Route path="/webinar" element={<WebinarRedirectPage />} />
      <Route path="/force-password-reset" element={<ProtectedRoute><ForcePasswordResetPage /></ProtectedRoute>} />

      {/* Protected: Participant */}
      <Route path="/dashboard" element={<ProtectedAppRoute><DashboardRouter /></ProtectedAppRoute>} />
      <Route path="/diagnostic" element={user ? <ProtectedAppRoute><DiagnosticPage /></ProtectedAppRoute> : <PublicLayout><PublicDiagnosticPage /></PublicLayout>} />
      <Route path="/challenge" element={user ? <ProtectedAppRoute><ChallengePage /></ProtectedAppRoute> : <PublicLayout><PublicProjectsPage /></PublicLayout>} />
      <Route path="/challenge-lab" element={<ProtectedAppRoute><ChallengeLabPage /></ProtectedAppRoute>} />
      <Route path="/challenge/showcase" element={user ? <ProtectedAppRoute><ShowcasePage /></ProtectedAppRoute> : <PublicLayout><ShowcasePage /></PublicLayout>} />
      <Route path="/project/:projectId" element={<ProtectedAppRoute><ProjectDetailPage /></ProtectedAppRoute>} />
      <Route path="/content" element={user ? <ProtectedAppRoute><ContentPage /></ProtectedAppRoute> : <PublicLayout><PublicContentPage /></PublicLayout>} />
      <Route path="/content/:contentId" element={<LegacyContentRedirect />} />
      <Route path="/contenido" element={user ? <Navigate to="/content" replace /> : <PublicLayout><PublicContentPage /></PublicLayout>} />
      <Route path="/modules" element={<Navigate to="/calendar" replace />} />
      <Route path="/modules/:id" element={<Navigate to="/calendar" replace />} />
      <Route path="/deliverables" element={<Navigate to="/calendar" replace />} />
      <Route path="/ai-tools" element={user ? <ProtectedAppRoute><AIToolsPage /></ProtectedAppRoute> : <PublicLayout><AIToolsPage isPublic /></PublicLayout>} />
      <Route path="/herramientas-ia" element={user ? <Navigate to="/ai-tools" replace /> : <PublicLayout><AIToolsPage isPublic /></PublicLayout>} />
      <Route path="/metrics" element={<ProtectedAppRoute><MetricsPage /></ProtectedAppRoute>} />
      <Route path="/calendar" element={<ProtectedAppRoute><CalendarPage /></ProtectedAppRoute>} />
      <Route path="/news" element={user ? <ProtectedAppRoute><NewsPage /></ProtectedAppRoute> : <PublicLayout><NewsPage /></PublicLayout>} />
      <Route path="/news/:articleId" element={user ? <ProtectedAppRoute><NewsPage /></ProtectedAppRoute> : <PublicLayout><NewsPage /></PublicLayout>} />
      <Route path="/forum" element={user ? <ProtectedAppRoute><ForumPage /></ProtectedAppRoute> : <PublicLayout><PublicForumPage /></PublicLayout>} />
      <Route path="/forum/:threadId" element={user ? <ProtectedAppRoute><ThreadDetailPage /></ProtectedAppRoute> : <PublicLayout><PublicForumPage /></PublicLayout>} />
      <Route path="/profile" element={<ProtectedAppRoute><ProfilePage /></ProtectedAppRoute>} />
      <Route path="/support" element={<ProtectedAppRoute><SupportPage /></ProtectedAppRoute>} />
      <Route path="/change-management" element={<ProtectedAppRoute><ChangeManagementPage /></ProtectedAppRoute>} />

      {/* Protected: Mentor */}
      <Route path="/students" element={<ProtectedAppRoute><StudentsPage /></ProtectedAppRoute>} />

      {/* Protected: Admin */}
      <Route path="/participants" element={<ProtectedAppRoute><MentorParticipantsPage /></ProtectedAppRoute>} />
      <Route path="/mentors" element={<ProtectedAppRoute><AdminDashboard /></ProtectedAppRoute>} />
      <Route path="/ai-portfolio" element={<ProtectedAppRoute><AIPortfolioPage /></ProtectedAppRoute>} />
      <Route path="/ai-portfolio/:projectId" element={<ProtectedAppRoute><AdminProjectDetailPage /></ProtectedAppRoute>} />
      <Route path="/ai-conversations-audit" element={<ProtectedAppRoute><AIConversationsAuditPage /></ProtectedAppRoute>} />
      <Route path="/enterprise-dashboard" element={<ProtectedAppRoute><EnterpriseDashboardPage /></ProtectedAppRoute>} />
      <Route path="/enterprise-diagnostic" element={<AdminRoute><EnterpriseDiagnosticPage /></AdminRoute>} />
      <Route path="/ai-inventory" element={<ProtectedAppRoute><AIInventoryPage /></ProtectedAppRoute>} />
      <Route path="/cohort-management" element={<ProtectedAppRoute><CohortManagementPage /></ProtectedAppRoute>} />
      <Route path="/user-management" element={<ProtectedAppRoute><UserManagementPage /></ProtectedAppRoute>} />
      <Route path="/user-tracking" element={<ProtectedAppRoute><UserTrackingPage /></ProtectedAppRoute>} />
      <Route path="/risk-management" element={<ProtectedAppRoute><RiskManagementPage /></ProtectedAppRoute>} />
      <Route path="/coe-management" element={<ProtectedAppRoute><CoEManagementPage /></ProtectedAppRoute>} />
      <Route path="/tickets" element={<ProtectedAppRoute><TicketManagementPage /></ProtectedAppRoute>} />
      <Route path="/settings" element={<ProtectedAppRoute><EnterpriseSettingsPage /></ProtectedAppRoute>} />
      <Route path="/metrics/diagnostic" element={<ProtectedAppRoute><DiagnosticMetricsPage /></ProtectedAppRoute>} />
      <Route path="/metrics/projects" element={<ProtectedAppRoute><ProjectMetricsPage /></ProtectedAppRoute>} />
      <Route path="/metrics/deliverables" element={<ProtectedAppRoute><DeliverablesMetricsPage /></ProtectedAppRoute>} />
      <Route path="/content-management" element={<ProtectedAppRoute><ContentManagementPage /></ProtectedAppRoute>} />
      <Route path="/bulk-completion" element={<SuperAdminRoute><BulkCompletionPage /></SuperAdminRoute>} />
      <Route path="/trends-management" element={<SuperAdminRoute><TrendsManagementPage /></SuperAdminRoute>} />
      <Route path="/trends-management/web" element={<SuperAdminRoute><WebTrendsPage /></SuperAdminRoute>} />
      
      <Route path="/ai-evaluations" element={<ProtectedAppRoute><AIEvaluationsPage /></ProtectedAppRoute>} />
      {/* A16: /resource-management SÍ queda con guard (AdminRoute + guard en el cuerpo). */}
      <Route path="/resource-management" element={<AdminRoute><ResourceManagementPage /></AdminRoute>} />
      <Route path="/template-management" element={<Navigate to="/resource-management" replace />} />
      
      <Route path="/program-management" element={<ProtectedAppRoute><ProgramManagementPage /></ProtectedAppRoute>} />
      <Route path="/route-interests" element={<SuperAdminRoute><RouteInterestsPage /></SuperAdminRoute>} />

      <Route path="/nps-dashboard" element={<ProtectedAppRoute><NPSDashboardPage /></ProtectedAppRoute>} />
      <Route path="/platform-config" element={<SuperAdminRoute><PlatformConfigPage /></SuperAdminRoute>} />
      <Route path="/admin/whatsapp-test" element={<SuperAdminRoute><WhatsAppTestPage /></SuperAdminRoute>} />
      <Route path="/admin/whatsapp-logs" element={<SuperAdminRoute><WhatsAppLogsPage /></SuperAdminRoute>} />
      <Route path="/organizations" element={<ProtectedAppRoute><OrganizationsPage /></ProtectedAppRoute>} />
      <Route path="/discount-codes" element={<ProtectedAppRoute><DiscountCodesPage /></ProtectedAppRoute>} />
      <Route path="/payment-management" element={<ProtectedAppRoute><PaymentsDashboardPage /></ProtectedAppRoute>} />
      <Route path="/live-sessions" element={<SuperAdminRoute><LiveSessionsAuditPage /></SuperAdminRoute>} />
      <Route path="/comunicados" element={<SuperAdminRoute><BulkEmailPage /></SuperAdminRoute>} />
      <Route path="/payouts" element={<SuperAdminRoute><PayoutsPage /></SuperAdminRoute>} />
      <Route path="/calendario-unificado" element={<MentorshipRoute><UnifiedCalendarPage /></MentorshipRoute>} />
      <Route path="/mentorias" element={<MentorshipRoute allowOrgAdmin><CustomMentorshipsPage /></MentorshipRoute>} />
      <Route path="/mentorias/:projectId" element={<MentorshipRoute allowOrgAdmin><MentorshipProjectDetailPage /></MentorshipRoute>} />
      <Route path="/mentorias/:projectId/grupo/:groupId" element={<MentorshipRoute allowOrgAdmin><MentorshipGroupDetailPage /></MentorshipRoute>} />
      <Route path="/payouts/:id" element={<SuperAdminRoute><PayoutDetailPage /></SuperAdminRoute>} />
      <Route path="/admin/executive" element={<SuperAdminRoute><ExecutiveDashboardPage /></SuperAdminRoute>} />
      <Route path="/admin/talleres" element={<SuperAdminRoute><ResourceWorkshopsListPage /></SuperAdminRoute>} />
      <Route path="/admin/talleres/:id" element={<SuperAdminRoute><ResourceWorkshopEditPage /></SuperAdminRoute>} />

      {/* Public workshop page (taller) */}
      <Route path="/taller/:slug" element={user ? <ProtectedAppRoute><ResourceWorkshopPage /></ProtectedAppRoute> : <PublicLayout><ResourceWorkshopPage /></PublicLayout>} />



      {/* Public routes */}
      <Route path="/tendencias" element={<Navigate to="/news" replace />} />
      <Route path="/rutas" element={user ? <ProtectedAppRoute><PublicCertificationsPage /></ProtectedAppRoute> : <PublicLayout><PublicCertificationsPage /></PublicLayout>} />
      <Route path="/rutas/:certificationId" element={user ? <ProtectedAppRoute><CertificationDetailPage /></ProtectedAppRoute> : <PublicLayout><CertificationDetailPage /></PublicLayout>} />
      <Route path="/planes" element={user ? <ProtectedAppRoute><PlansPage /></ProtectedAppRoute> : <PublicLayout><PlansPage /></PublicLayout>} />

      <Route path="/empresas" element={user ? <ProtectedAppRoute><EnterprisePage /></ProtectedAppRoute> : <PublicLayout><EnterprisePage /></PublicLayout>} />
      <Route path="/diagnostico-empresarial" element={user ? <ProtectedAppRoute><EnterpriseDiagnosticLandingPage /></ProtectedAppRoute> : <PublicLayout><EnterpriseDiagnosticLandingPage /></PublicLayout>} />
      <Route path="/registro-empresa" element={user ? <Navigate to="/enterprise-diagnostic" replace /> : <PublicLayout><EnterpriseSignupPage /></PublicLayout>} />
      <Route path="/impacto" element={user ? <ProtectedAppRoute><ImpactPage /></ProtectedAppRoute> : <PublicLayout><ImpactPage /></PublicLayout>} />
      <Route path="/impact-management" element={<SuperAdminRoute><ImpactManagementPage /></SuperAdminRoute>} />
      <Route path="/admin/adquisicion" element={<SuperAdminRoute><AcquisitionDashboardPage /></SuperAdminRoute>} />
      <Route path="/privacy-policy" element={<PublicLayout><PrivacyPolicyPage /></PublicLayout>} />
      <Route path="/terms-of-service" element={<PublicLayout><TermsOfServicePage /></PublicLayout>} />
      <Route path="/acerca" element={user ? <ProtectedAppRoute><AboutPage /></ProtectedAppRoute> : <PublicLayout><AboutPage /></PublicLayout>} />

      {/* Enrollment flow (always public) */}
      <Route path="/inscripcion/:slug/:cohortId" element={<PublicLayout><EnrollmentPage /></PublicLayout>} />
      <Route path="/inscripcion/resultado" element={<PublicLayout><EnrollmentResultPage /></PublicLayout>} />

      {/* Public certificate verification */}
      <Route path="/c/:certLogId" element={<CertificateVerifyPage />} />

      {/* Legacy URL redirects — mapea URLs del sitio anterior (Softr/WordPress)
          a las páginas actuales. Preserva SEO y backlinks históricos. Ver
          Search Console → Soft 404 reports (2026-07-30). */}
      <Route path="/login" element={<Navigate to="/auth" replace />} />
      <Route path="/sign-up" element={<Navigate to="/auth?mode=signup" replace />} />
      <Route path="/about" element={<Navigate to="/acerca" replace />} />
      <Route path="/terminos-condiciones" element={<Navigate to="/terms-of-service" replace />} />
      <Route path="/certificacion" element={<Navigate to="/rutas" replace />} />
      <Route path="/certificaciones" element={<Navigate to="/rutas" replace />} />
      <Route path="/certificaciones/:certificationId" element={<LegacyCertificationRedirect />} />

      <Route path="/suscripcion" element={<Navigate to="/planes" replace />} />
      <Route path="/masterclass" element={<Navigate to="/" replace />} />
      <Route path="/consultoria" element={<Navigate to="/empresas" replace />} />
      <Route path="/unete" element={<Navigate to="/acerca" replace />} />
      <Route path="/comunidad" element={<Navigate to="/news" replace />} />
      <Route path="/comunidad/blog" element={<Navigate to="/news" replace />} />
      <Route path="/comunidad/blog/:slug" element={<Navigate to="/news" replace />} />
      <Route path="/blog3/:slug" element={<Navigate to="/news" replace />} />
      <Route path="/forum/thread/:threadId" element={<Navigate to="/forum" replace />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

const App = () => (
  <Sentry.ErrorBoundary fallback={
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background text-foreground">
      <h2 className="text-xl font-semibold">Ocurrió un error inesperado</h2>
      <p className="text-muted-foreground">El error ha sido reportado automáticamente.</p>
      <button
        className="px-4 py-2 bg-primary text-primary-foreground rounded-md"
        onClick={() => window.location.reload()}
      >
        Recargar página
      </button>
    </div>
  }>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
              <NotificationProvider>
                <AppRoutes />
              </NotificationProvider>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default Sentry.withProfiler(App);
